import React, { FC, RefObject } from 'react';
import { addMonths, isBefore, lastDayOfMonth, startOfDay, startOfMonth } from 'date-fns';
import { breakpointMatches } from 'app/functions/grid';
import { Calendar } from '@flixbus/honeycomb-react';
import * as styles from './DayPicker.scss';
import { addYears } from 'date-fns';
import { useCalendarData } from './useCalendarData';
import { getSelectedDay } from 'app/functions/date/getSelectedDay';
import { useAppSelector } from 'app/hooks/appHooks';
import { dateLabelSelector } from 'app/store/selectors';

type DayPicker = {
  monthScroll: number;
  minDepartureDate: Date;
  departureDate: Date;
  returnDate: Date;
  returnDateVisible: boolean;
  departureFocused: boolean;
  returnFocused: boolean;
  setDepartureFocused: (isFocused: boolean) => void;
  setReturnFocused: (isFocused: boolean, emit?: boolean) => void;
  onDepartureDateChange: (date: Date) => void;
  onReturnDateChange: (date: Date) => void;
  afterSelectFocusAction: ({ focusedItem }: { focusedItem: 'departure' | 'arrival' }) => void;
  returnInput: RefObject<HTMLInputElement>;
};

export const DayPicker: FC<DayPicker> = ({
  monthScroll,
  minDepartureDate,
  departureDate,
  returnDate,
  returnDateVisible,
  departureFocused,
  returnFocused,
  setDepartureFocused,
  setReturnFocused,
  onDepartureDateChange,
  onReturnDateChange,
  afterSelectFocusAction,
  returnInput,
}) => {
  const validDayForClick = (date: Date) => {
    return !isBefore(startOfDay(date), startOfDay(minDepartureDate));
  };

  const handleSelect = (date: Date) => {
    // Day click handler when there is only one date
    // After the date is changed, close the datepicker.
    // Only close the date picker now because there is
    // only one date to choose.
    if (departureFocused && !returnDateVisible) {
      if (!validDayForClick(date)) {
        return;
      }

      onDepartureDateChange(date);

      if (!breakpointMatches('small').matches) {
        setDepartureFocused(false);
      }
      afterSelectFocusAction({ focusedItem: 'departure' });
    }

    // Day click handler when choosing the from date.
    // In this case, change the date and then switch
    // to the second datepicker.
    if (departureFocused && returnDateVisible) {
      if (!validDayForClick(date)) {
        return;
      }

      // this.lastMonth = this.dayPicker.state.currentMonth;

      onDepartureDateChange(date);
      setDepartureFocused(false);
      setReturnFocused(true);
      if (!breakpointMatches('small').matches) {
        returnInput.current?.focus();
      }
    }

    // Day click handler for the to date.
    if (returnFocused) {
      if (!validDayForClick(date)) {
        return;
      }

      // In this case, set the departure date.
      if (isBefore(startOfDay(date), startOfDay(departureDate))) {
        return;
      }

      onReturnDateChange(date);

      if (breakpointMatches('small').matches) {
        setDepartureFocused(true);
        setReturnFocused(false, false);
      } else {
        setReturnFocused(false);
      }

      afterSelectFocusAction({ focusedItem: 'arrival' });
    }
  };

  let startDate;
  const firstDayOfMonth = startOfMonth(
    addMonths(returnFocused ? returnDate : departureDate, monthScroll),
  );
  if (returnFocused) {
    // block all before departure day for arrival
    if (isBefore(firstDayOfMonth, departureDate)) {
      startDate = departureDate;
      // if 1 available month shown
    } else if (isBefore(firstDayOfMonth, minDepartureDate)) {
      startDate = minDepartureDate;
    } else {
      // if scrolled
      startDate = firstDayOfMonth;
    }
  } else {
    if (isBefore(firstDayOfMonth, minDepartureDate)) {
      startDate = minDepartureDate;
    } else {
      // if scrolled
      startDate = firstDayOfMonth;
    }
  }

  const endDate = lastDayOfMonth(addMonths(startDate, 1));

  const calendarData = useCalendarData();

  const dayLabel = useAppSelector(dateLabelSelector);

  return (
    <Calendar
      firstDayOfWeek={calendarData?.firstDayOfWeek}
      id="calendar"
      extraClasses={styles.calendar}
      handleSelect={handleSelect}
      startDate={breakpointMatches('small').matches ? minDepartureDate : startDate}
      endDate={breakpointMatches('small').matches ? addYears(minDepartureDate, 1) : endDate}
      selected={returnDateVisible ? undefined : departureDate}
      startSelected={returnDateVisible ? departureDate : undefined}
      endSelected={returnDateVisible ? returnDate : undefined}
      months={calendarData?.months}
      days={calendarData?.days}
      dayLabel={(day: number, month: number, year: number) => {
        const dayDate = getSelectedDay({ day, month, year });
        return dayLabel(dayDate);
      }}
    />
  );
};
